import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import FileItem from "./FileItem.tsx";
import SelectedContextItem from "./SelectedContextItem.tsx";
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";

interface FilesDropdownProps {}

const FilesDropdown: React.FC<FilesDropdownProps> = () => {
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { openFileStatuses, focusedFile, selectedAdditionalIdeContext } = dynamicStateObj;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment, vscode } = environmentObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateSelectedAdditionalIdeContext = useAppStore((state: any) => state.updateSelectedAdditionalIdeContext);

    const isFileInSelectedContext = (filePath: string) => {
        // Check if file is directly selected in context
        const isDirectlySelected = selectedAdditionalIdeContext?.files?.some(
            (file: any) => file.filePath === filePath && file.isSelected
        );
        if (isDirectlySelected) return true;

        // Check if file is in a selected folder
        return selectedAdditionalIdeContext?.folders?.some(
            (folder: any) => folder.isSelected && filePath.startsWith(folder.filePath)
        );
    };

    const handleAddContextClick = () => {
        if (environment.includes("jetbrains")) {
            window.jetbrainsPostMessage(JSON.stringify({
                functionName: "getFilesAndFolders",
                data: []
            }));
        }
        else if (environment === "vscode") {
            vscode.postMessage({
                functionName: "getFilesAndFolders",
                data: []
            });
        }
        setDynamicStateObj({ visiblePopup: "fileSearch" });
    };

    const handleContextItemRemove = (item: any, type: 'files' | 'folders') => {
        updateSelectedAdditionalIdeContext({ item: item.filePath, type }, "delete");
    };

    const handleContextItemToggle = (item: any, type: 'files' | 'folders') => {
        updateSelectedAdditionalIdeContext({ item: item.filePath, type }, "toggle");
    };

    const shouldShowSelectDeselectButtons = (openFileStatuses.length + (selectedAdditionalIdeContext?.files?.length || 0) + (selectedAdditionalIdeContext?.folders?.length || 0)) >= 3;
    
    const handleSelectAll = () => {
        // Update open files
        const newFileStatuses = openFileStatuses.map((fileStatus: any) => {
            return {...fileStatus, isSelected: true};
        });

        // Update context items
        const newContextFiles = selectedAdditionalIdeContext?.files?.map((file: any) => ({
            ...file,
            isSelected: true
        })) || [];

        const newContextFolders = selectedAdditionalIdeContext?.folders?.map((folder: any) => ({
            ...folder,
            isSelected: true
        })) || [];

        setDynamicStateObj({
            openFileStatuses: newFileStatuses,
            selectedAdditionalIdeContext: {
                ...selectedAdditionalIdeContext,
                files: newContextFiles,
                folders: newContextFolders
            }
        });
    };

    const handleDeselectAll = () => {
        // Update open files
        const newFileStatuses = openFileStatuses.map((fileStatus: any) => {
            return {...fileStatus, isSelected: false};
        });

        // Update context items
        const newContextFiles = selectedAdditionalIdeContext?.files?.map((file: any) => ({
            ...file,
            isSelected: false
        })) || [];

        const newContextFolders = selectedAdditionalIdeContext?.folders?.map((folder: any) => ({
            ...folder,
            isSelected: false
        })) || [];

        setDynamicStateObj({
            openFileStatuses: newFileStatuses,
            selectedAdditionalIdeContext: {
                ...selectedAdditionalIdeContext,
                files: newContextFiles,
                folders: newContextFolders
            }
        });
    };

    const alignmentClass = environment === "vscode" ? "right-0" : "left-0";

    return (
        <div
            className={`w-56 -ml-1 border border-neutral-800 mt-1 bg-neutral-100 dark:bg-neutral-900 rounded-lg shadow-lg absolute top-full ${alignmentClass} cursor-pointer flex flex-col`}>
            <div className="max-h-72 overflow-y-auto flex-1">
                {selectedAdditionalIdeContext?.folders?.map((folder: any, index: number) => (
                    <SelectedContextItem
                        key={folder.filePath}
                        item={folder}
                        onRemove={() => handleContextItemRemove(folder, 'folders')}
                        onToggle={() => handleContextItemToggle(folder, 'folders')}
                        isLastItem={index === (selectedAdditionalIdeContext.folders?.length || 0) - 1 && !selectedAdditionalIdeContext.files?.length}
                    />
                ))}
                {selectedAdditionalIdeContext?.files?.map((file: any, index: number) => (
                    <SelectedContextItem
                        key={file.filePath}
                        item={file}
                        onRemove={() => handleContextItemRemove(file, 'files')}
                        onToggle={() => handleContextItemToggle(file, 'files')}
                        isLastItem={index === (selectedAdditionalIdeContext.files?.length || 0) - 1}
                    />
                ))}
                {openFileStatuses.map((fileStatus: any, index: number) => (
                    <FileItem
                        key={fileStatus.filePath}
                        fileStatus={fileStatus}
                        isLastItem={index === openFileStatuses.length - 1}
                        focusedFile={focusedFile}
                        isFirstFileItem={index === 0}
                        isInSelectedContext={isFileInSelectedContext(fileStatus.filePath)}
                        hasContextItems={!!(selectedAdditionalIdeContext?.files?.length || selectedAdditionalIdeContext?.folders?.length)}
                    />
                ))}
            </div>
            {shouldShowSelectDeselectButtons && (
                <div className="flex justify-between border-t border-neutral-300 text-xs dark:border-neutral-700 py-2">
                    <button
                        onClick={handleSelectAll}
                        className="w-20 text-blue-700 dark:text-white h-6 ml-2.5 rounded-lg backdrop-blur-lg bg-blue-100 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-400/20 hover:bg-blue-200 dark:hover:bg-blue-400/20 transition-colors"
                    >
                        Select All
                    </button>
                    <button
                        onClick={handleDeselectAll}
                        className="w-20 text-red-700 dark:text-white h-6 mr-2.5 rounded-lg backdrop-blur-lg bg-red-100 dark:bg-red-500/10 border border-red-300 dark:border-red-400/20 hover:bg-red-200 dark:hover:bg-red-400/20 transition-colors"
                    >
                        Deselect All
                    </button>
                </div>
            )}
            <div className="flex items-center justify-center px-4 py-2 text-black dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 border-t border-neutral-300 dark:border-neutral-700" onClick={handleAddContextClick}>
                <button className="flex items-center gap-2 text-sm">
                    <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                    <span>Add Context</span>
                </button>
            </div>
        </div>
    );
};

export default FilesDropdown;